import {
  BarChart3,
  Coins,
  Eye,
  Gauge,
  MousePointerClick,
  Percent,
  ShoppingBag,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { TFunction } from "i18next";
import type { DefinitionTooltipInfo } from "src/components/common";
import type { PaidMediaOverviewPlatform } from "src/services/PaidMediaOverviewAPI";
import type {
  PlatformReportRow,
  PlatformReportTotals,
} from "../../data/paidMediaPlatformReportMockData";

export type PlatformReportMetric = "spend" | "revenue" | "purchases" | "roas";

export type PlatformReportValueFormat = "money" | "unitCost" | "count" | "percent" | "multiplier";

export interface PlatformReportKpiDefinition {
  id: keyof PlatformReportTotals;
  labelKey: string;
  icon: LucideIcon;
  format: PlatformReportValueFormat;
  /** `paidMediaOverview.tooltips.*` id, shared with the overview cards. */
  tooltipId: string;
  lowerIsBetter?: boolean;
  /** Replaced by the platform's own click wording (swipe ups, destination clicks). */
  useClickLabel?: boolean;
}

export const PLATFORM_REPORT_KPIS: PlatformReportKpiDefinition[] = [
  { id: "spend", labelKey: "platformAdsReport.kpis.spend", icon: Wallet, format: "money", tooltipId: "totalSpend" },
  { id: "impressions", labelKey: "platformAdsReport.kpis.impressions", icon: Eye, format: "count", tooltipId: "impressions" },
  {
    id: "clicks",
    labelKey: "platformAdsReport.kpis.clicks",
    icon: MousePointerClick,
    format: "count",
    useClickLabel: true,
    tooltipId: "clicks",
  },
  { id: "ctr", labelKey: "platformAdsReport.kpis.ctr", icon: Percent, format: "percent", tooltipId: "ctr" },
  { id: "cpc", labelKey: "platformAdsReport.kpis.cpc", icon: Coins, format: "unitCost", lowerIsBetter: true, tooltipId: "cpc" },
  { id: "purchases", labelKey: "platformAdsReport.kpis.purchases", icon: ShoppingBag, format: "count", tooltipId: "paidPurchases" },
  { id: "revenue", labelKey: "platformAdsReport.kpis.revenue", icon: TrendingUp, format: "money", tooltipId: "paidRevenue" },
  { id: "roas", labelKey: "platformAdsReport.kpis.roas", icon: Gauge, format: "multiplier", tooltipId: "roas" },
  {
    id: "costPerPurchase",
    labelKey: "platformAdsReport.kpis.costPerPurchase",
    icon: Coins,
    format: "unitCost",
    lowerIsBetter: true,
    tooltipId: "blendedCpp",
  },
  {
    id: "conversionRate",
    labelKey: "platformAdsReport.kpis.conversionRate",
    icon: BarChart3,
    format: "percent",
    tooltipId: "clickToPurchase",
  },
];

export const PLATFORM_REPORT_METRIC_OPTIONS: Array<{
  value: PlatformReportMetric;
  labelKey: string;
  format: PlatformReportValueFormat;
}> = [
  { value: "spend", labelKey: "platformAdsReport.kpis.spend", format: "money" },
  { value: "revenue", labelKey: "platformAdsReport.kpis.revenue", format: "money" },
  { value: "purchases", labelKey: "platformAdsReport.kpis.purchases", format: "count" },
  { value: "roas", labelKey: "platformAdsReport.kpis.roas", format: "multiplier" },
];

export type PlatformReportSortKey =
  | "name"
  | "spend"
  | "impressions"
  | "clicks"
  | "ctr"
  | "cpc"
  | "purchases"
  | "revenue"
  | "roas"
  | "costPerPurchase";

export const PLATFORM_REPORT_SORT_GETTERS: Record<
  PlatformReportSortKey,
  (_row: PlatformReportRow) => string | number | null
> = {
  name: row => row.name,
  spend: row => row.spend,
  impressions: row => row.impressions,
  clicks: row => row.clicks,
  ctr: row => row.ctr,
  cpc: row => row.cpc,
  purchases: row => row.purchases,
  revenue: row => row.revenue,
  roas: row => row.roas,
  costPerPurchase: row => row.costPerPurchase,
};

interface PlatformReportEntityKeys {
  groupLabelKey: string;
  creativeLabelKey: string;
  groupColumnKey: string;
}

const DEFAULT_ENTITY_KEYS: PlatformReportEntityKeys = {
  groupLabelKey: "platformAdsReport.entities.adGroups",
  creativeLabelKey: "platformAdsReport.entities.creatives",
  groupColumnKey: "platformAdsReport.columns.adGroup",
};

const ENTITY_KEYS: Partial<Record<PaidMediaOverviewPlatform, PlatformReportEntityKeys>> = {
  meta_ads: {
    groupLabelKey: "platformAdsReport.entities.adSets",
    creativeLabelKey: "platformAdsReport.entities.ads",
    groupColumnKey: "platformAdsReport.columns.adSet",
  },
  snapchat_ads: {
    groupLabelKey: "platformAdsReport.entities.adSquads",
    creativeLabelKey: "platformAdsReport.entities.ads",
    groupColumnKey: "platformAdsReport.columns.adSquad",
  },
};

export function platformReportEntityKeys(
  platform: PaidMediaOverviewPlatform
): PlatformReportEntityKeys {
  return ENTITY_KEYS[platform] ?? DEFAULT_ENTITY_KEYS;
}

export const PLATFORM_REPORT_STATUS_LABEL_KEY: Record<string, string> = {
  active: "platformAdsReport.statuses.active",
  learning: "platformAdsReport.statuses.learning",
  paused: "platformAdsReport.statuses.paused",
};

export const PLATFORM_REPORT_PAGE_SIZE = 8;
export const PLATFORM_REPORT_CHART_ENTRIES = 5;

/** The Meta and Google reports' metric palette, reused so every report matches. */
export const PLATFORM_REPORT_METRIC_COLORS = {
  spend: "#2b74e4",
  impressions: "#36cdb0",
  clicks: "#f6c15f",
  conversions: "#8d4fd3",
} as const;

export type PlatformReportTrendMetric = "spend" | "impressions" | "clicks" | "purchases";

/** Mirrors the Meta report's trend chips: metric, colour and which axis it uses. */
export const PLATFORM_REPORT_TREND_METRICS: Array<{
  key: PlatformReportTrendMetric;
  color: string;
  axis: "primary" | "secondary";
  format: PlatformReportValueFormat;
}> = [
  { key: "spend", color: PLATFORM_REPORT_METRIC_COLORS.spend, axis: "primary", format: "money" },
  {
    key: "impressions",
    color: PLATFORM_REPORT_METRIC_COLORS.impressions,
    axis: "primary",
    format: "count",
  },
  {
    key: "clicks",
    color: PLATFORM_REPORT_METRIC_COLORS.clicks,
    axis: "secondary",
    format: "count",
  },
  {
    key: "purchases",
    color: PLATFORM_REPORT_METRIC_COLORS.conversions,
    axis: "secondary",
    format: "count",
  },
];

/**
 * Section help reuses the paid media report copy already translated for the
 * Meta and Google tabs, so every report explains its sections identically.
 */
export function platformReportSectionHelp(
  t: TFunction,
  key: "performanceTrends" | "conversionFunnel" | "roasByCampaign" | "spendDistributionByCampaign" | "campaignPerformance" | "adSets" | "creativePerformance"
): DefinitionTooltipInfo {
  const description = t(`metaAdsReport.sectionHelp.${key}.description`, { defaultValue: "" });
  const formula = t(`metaAdsReport.sectionHelp.${key}.formula`, { defaultValue: "" });
  return formula ? { description, formula } : { description };
}

export type PlatformReportTableTab = "campaigns" | "groups" | "creatives";

export const PLATFORM_REPORT_TABLE_TABS: PlatformReportTableTab[] = [
  "campaigns",
  "groups",
  "creatives",
];
